// googleTool.js — read-only Google (Calendar / Gmail / Drive) as agent tools.
//
// Closes the audit gap "token salvo ≠ ferramenta no turno": once the user connects
// Google in Integrações, the agent can actually SEE the account. Each tool calls the
// Worker (/api/google/*), which refreshes the OAuth token on demand (googleToken.js)
// and hits Google with READ-ONLY scopes. No send/modify here — those stay in the
// text chat where there is an approval UI. If the account isn't connected the Worker
// returns a clear "conecte em Integrações", which the model relays.

import { apiFetch } from './api.js';

// ── Pure formatters (model-facing strings) ───────────────────────────────
export function formatCalendar(events) {
  const list = Array.isArray(events) ? events : [];
  if (!list.length) return '[google_calendar] Nenhum evento próximo.';
  const lines = list.map((e) => {
    const when = e.start || '(sem data)';
    const loc = e.location ? ` · ${e.location}` : '';
    const link = e.hangoutLink ? ` · ${e.hangoutLink}` : '';
    return `• ${when} — ${e.summary || '(sem título)'}${loc}${link}`;
  });
  return `[google_calendar] próximos ${list.length}:\n${lines.join('\n')}`;
}

export function formatGmail(messages, query) {
  const list = Array.isArray(messages) ? messages : [];
  const head = query ? `[gmail_search "${query}"]` : '[gmail_search]';
  if (!list.length) return `${head} Nenhuma mensagem.`;
  const lines = list.map((m) => {
    const from = m.from ? ` — de ${m.from}` : '';
    const snip = m.snippet ? `\n   ${m.snippet}` : '';
    return `• ${m.subject || '(sem assunto)'}${from}${m.date ? ` (${m.date})` : ''}${snip}`;
  });
  return `${head} ${list.length} resultado(s):\n${lines.join('\n')}`;
}

export function formatDrive(files, query) {
  const list = Array.isArray(files) ? files : [];
  const head = query ? `[drive_search "${query}"]` : '[drive_search]';
  if (!list.length) return `${head} Nenhum arquivo.`;
  const lines = list.map((f) => {
    const owner = f.owner ? ` · ${f.owner}` : '';
    const link = f.link ? ` · ${f.link}` : '';
    return `• ${f.name}${owner}${link}`;
  });
  return `${head} ${list.length} arquivo(s):\n${lines.join('\n')}`;
}

function fail(res, fallback) {
  // Worker err() → { error, code }. NOT_CONNECTED already reads "conecte em Integrações".
  return `Erro: ${res?.error || res?.data?.error || fallback}`;
}

// ── Tool defs ────────────────────────────────────────────────────────────
const GOOGLE_CALENDAR_DEF = {
  type: 'function',
  function: {
    name: 'google_calendar_upcoming',
    description:
      'Lista os próximos eventos do Google Calendar do usuário (somente leitura). '
      + 'Use quando ele perguntar sobre a agenda, "o que tenho hoje/essa semana", reuniões ou compromissos. '
      + 'Requer Google Calendar conectado em Integrações. Ex: {"max":5}.',
    parameters: {
      type: 'object',
      properties: { max: { type: 'integer', description: 'quantos eventos (1-25, padrão 10)' } },
    },
  },
};

const GMAIL_SEARCH_DEF = {
  type: 'function',
  function: {
    name: 'gmail_search',
    description:
      'Busca mensagens no Gmail do usuário (somente metadados: remetente, assunto, data, trecho — nunca o corpo completo, nunca envia). '
      + 'Use para "tem e-mail do cliente X?", "o que chegou hoje", achar uma thread. Aceita a sintaxe de busca do Gmail '
      + '(ex: "from:cliente is:unread newer_than:7d"). Requer Gmail conectado em Integrações. Ex: {"q":"from:glyco","max":5}.',
    parameters: {
      type: 'object',
      properties: {
        q: { type: 'string', description: 'query no formato de busca do Gmail' },
        max: { type: 'integer', description: 'quantas mensagens (1-25, padrão 10)' },
      },
    },
  },
};

const DRIVE_SEARCH_DEF = {
  type: 'function',
  function: {
    name: 'google_drive_search',
    description:
      'Busca arquivos no Google Drive do usuário por texto (nome + conteúdo; somente leitura, devolve nome + link). '
      + 'Use para "acha o deck de X", "onde está a planilha Y". Requer Google Drive conectado em Integrações. '
      + 'Ex: {"q":"proposta glyco","max":5}.',
    parameters: {
      type: 'object',
      properties: {
        q: { type: 'string', description: 'texto a buscar em nome/conteúdo' },
        max: { type: 'integer', description: 'quantos arquivos (1-25, padrão 10)' },
      },
    },
  },
};

function clampMaxParam(max) {
  const n = Number.parseInt(max, 10);
  if (!Number.isFinite(n) || n <= 0) return 10;
  return Math.min(n, 25);
}

// ── Executors (never throw; return model-facing string) ──────────────────
export async function executeCalendarUpcoming(args) {
  const max = clampMaxParam(args?.max);
  const res = await apiFetch(`/api/google/calendar/upcoming?max=${max}`);
  if (!res.ok) return fail(res, 'não consegui ler o Google Calendar.');
  return formatCalendar(res.data?.events);
}

export async function executeGmailSearch(args) {
  const q = typeof args?.q === 'string' ? args.q.trim() : '';
  const max = clampMaxParam(args?.max);
  const res = await apiFetch(`/api/google/gmail/search?q=${encodeURIComponent(q)}&max=${max}`);
  if (!res.ok) return fail(res, 'não consegui buscar no Gmail.');
  return formatGmail(res.data?.messages, q);
}

export async function executeDriveSearch(args) {
  const q = typeof args?.q === 'string' ? args.q.trim() : '';
  const max = clampMaxParam(args?.max);
  const res = await apiFetch(`/api/google/drive/search?q=${encodeURIComponent(q)}&max=${max}`);
  if (!res.ok) return fail(res, 'não consegui buscar no Drive.');
  return formatDrive(res.data?.files, q);
}

/**
 * extraTool resolver: Google read-only tools, available in local sessions (chat E
 * code). Fora do browser (sem window) → [] (contrato dos extraTools). São read-only
 * (sem gate de aprovação); se o provider não estiver conectado o Worker devolve a
 * orientação de conectar, que o modelo repassa.
 */
export function resolveGoogleTools() {
  if (typeof window === 'undefined') return [];
  return [
    { def: GOOGLE_CALENDAR_DEF, execute: (a) => executeCalendarUpcoming(a) },
    { def: GMAIL_SEARCH_DEF, execute: (a) => executeGmailSearch(a) },
    { def: DRIVE_SEARCH_DEF, execute: (a) => executeDriveSearch(a) },
  ];
}
